import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_AVATAR, type AvatarConfig } from '@qq/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { childPhotoUrl, sessionKey, useSession } from '../lib/session.js';
import { Avatar } from '../art/Avatar.js';
import { Companion } from '../art/Companion.js';
import { SelfieCapture } from './SelfieCapture.js';

interface WardrobeOption {
  key: string;
  label: string;
  locked: boolean;
  unlock: string;
}
interface Wardrobe {
  parts: { part: string; label: string; options: WardrobeOption[] }[];
  companions: { key: string; name: string; blurb: string; locked: boolean }[];
  photoLooks: readonly { key: string; label: string }[];
  photosAllowed: boolean;
}

type Tab = 'look' | 'friend' | 'photo';

/**
 * Where a child builds their character. Big targets, live preview, no save
 * button anxiety: every tap previews immediately and Save is one obvious
 * button. Locked pieces are visible with a padlock, because seeing what you
 * could earn is the point - but the server decides, not this screen.
 */
export function CharacterBuilder() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useSession();
  const child = session.data?.child;

  const wardrobe = useQuery({
    queryKey: ['wardrobe'],
    queryFn: () => api<Wardrobe>('/child/me/wardrobe'),
    enabled: Boolean(child),
  });

  const [draft, setDraft] = useState<AvatarConfig | null>(null);
  const [companion, setCompanion] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('look');
  const [activePart, setActivePart] = useState('skin');
  const [capturing, setCapturing] = useState(false);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/child/me', { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });

  const savePhoto = useMutation({
    mutationFn: (dataUrl: string) => api('/child/me/photo', { method: 'POST', body: { dataUrl } }),
    onSuccess: () => {
      setCapturing(false);
      void qc.invalidateQueries({ queryKey: sessionKey });
    },
  });

  const removePhoto = useMutation({
    mutationFn: () => api('/child/me/photo', { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });

  if (session.isPending) return <main className="screen">Just a moment…</main>;
  if (!child) return <Navigate to="/" replace />;

  const config = draft ?? child.avatarConfig ?? DEFAULT_AVATAR;
  const chosenCompanion = companion ?? child.companionId;
  const showingPhoto = child.avatarKind === 'photo' && child.hasPhoto;
  const photoUrl = child.hasPhoto ? childPhotoUrl(child.id, child.photoUpdatedAt) : null;

  const pick = (part: string, key: string) => setDraft({ ...config, [part]: key } as AvatarConfig);

  const currentPart = wardrobe.data?.parts.find((p) => p.part === activePart);
  const dirty = draft !== null || companion !== null;

  if (capturing) {
    return (
      <main className="screen">
        <SelfieCapture
          saving={savePhoto.isPending}
          error={savePhoto.error instanceof ApiRequestError ? savePhoto.error.body.message : null}
          onSave={(dataUrl) => savePhoto.mutate(dataUrl)}
          onCancel={() => setCapturing(false)}
        />
      </main>
    );
  }

  return (
    <main className="screen builder">
      <div className="builder-preview">
        <Avatar
          config={config}
          size={200}
          photoUrl={showingPhoto ? photoUrl : null}
          title={`${child.nickname}'s character`}
        />
        <Companion companionKey={chosenCompanion} size={92} />
      </div>

      <div className="tab-row">
        <button
          type="button"
          className="chip"
          data-selected={tab === 'look'}
          onClick={() => setTab('look')}
        >
          My look
        </button>
        <button
          type="button"
          className="chip"
          data-selected={tab === 'friend'}
          onClick={() => setTab('friend')}
        >
          My friend
        </button>
        {wardrobe.data?.photosAllowed && (
          <button
            type="button"
            className="chip"
            data-selected={tab === 'photo'}
            onClick={() => setTab('photo')}
          >
            My photo
          </button>
        )}
      </div>

      {tab === 'look' && (
        <>
          <div className="tab-row">
            {wardrobe.data?.parts.map((part) => (
              <button
                key={part.part}
                type="button"
                className="chip"
                data-selected={activePart === part.part}
                onClick={() => setActivePart(part.part)}
              >
                {part.label}
              </button>
            ))}
          </div>

          <div className="option-grid">
            {currentPart?.options.map((option) => (
              <button
                key={option.key}
                type="button"
                className="option-tile"
                data-selected={config[activePart as keyof AvatarConfig] === option.key}
                disabled={option.locked}
                onClick={() => pick(activePart, option.key)}
              >
                <Avatar
                  config={{ ...config, [activePart]: option.key } as AvatarConfig}
                  size={72}
                  title={option.label}
                />
                <span>{option.locked ? `🔒 ${option.label}` : option.label}</span>
              </button>
            ))}
          </div>
          {currentPart?.options.some((o) => o.locked) && (
            <p className="muted">
              Locked pieces are waiting in collections you have not finished yet.
            </p>
          )}
        </>
      )}

      {tab === 'friend' && (
        <div className="option-grid">
          {wardrobe.data?.companions.map((c) => (
            <button
              key={c.key}
              type="button"
              className="option-tile"
              data-selected={chosenCompanion === c.key}
              onClick={() => setCompanion(c.key)}
            >
              <Companion companionKey={c.key} size={84} title={c.name} />
              <span>{c.name}</span>
              <small className="muted">{c.blurb}</small>
            </button>
          ))}
        </div>
      )}

      {tab === 'photo' && (
        <section className="card card--soft">
          <p>
            You can take a photo of yourself to be your character. It stays on your own family
            computer at home — nobody else can see it.
          </p>
          <div className="selfie-actions">
            <button type="button" className="btn" onClick={() => setCapturing(true)}>
              {child.hasPhoto ? 'Take a new photo' : 'Take a photo'}
            </button>
            {child.hasPhoto && (
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => save.mutate({ avatarKind: showingPhoto ? 'drawn' : 'photo' })}
              >
                {showingPhoto ? 'Use my drawn character' : 'Use my photo'}
              </button>
            )}
          </div>
          {child.hasPhoto && (
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => removePhoto.mutate()}
              disabled={removePhoto.isPending}
            >
              Delete my photo
            </button>
          )}
        </section>
      )}

      {save.error instanceof ApiRequestError && (
        <p className="gentle-note">{save.error.body.message}</p>
      )}

      <div className="selfie-actions">
        <button
          type="button"
          className="btn"
          disabled={!dirty || save.isPending}
          onClick={() =>
            save.mutate(
              {
                ...(draft ? { avatarConfig: draft } : {}),
                ...(companion ? { companionId: companion } : {}),
              },
              {
                onSuccess: () => {
                  setDraft(null);
                  setCompanion(null);
                },
              },
            )
          }
        >
          {save.isPending ? 'Saving…' : 'Save my character'}
        </button>
        <button type="button" className="btn btn--outline" onClick={() => navigate('/home')}>
          Done
        </button>
      </div>
    </main>
  );
}
